import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { type SerializableRecord } from '@post-office/serializable-record';
import { camelCase } from 'lodash';
import { useEffect, useState } from 'react';

import { type State } from './types';
import { assertResponseIsOk, toData, toError, toLoading } from './utils';

type Options = {
	analytics?: {
		name: string;
	} & Record<string, unknown>;
};

const processedResponses = new Map<Response, unknown>();

export const useData = <Data extends SerializableRecord>(
	promiseToResolve: Promise<Response> | undefined,
	options?: Options,
): State<Data> => {
	const [state, setState] = useState<State<Data>>(toLoading());

	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { name: analyticsSubjectId, ...analyticsAttributes } = options?.analytics ?? {
		name: 'unknown',
	};

	useEffect(() => {
		if (!promiseToResolve) return;

		void (async () => {
			try {
				setState(toLoading());
				const response = await promiseToResolve;

				// if we came across this response before, just use previously parsed body
				if (processedResponses.has(response)) {
					setState(toData(processedResponses.get(response) as Data));
					return;
				}

				// otherwise do the full processing
				assertResponseIsOk(response);
				// we clone the response to allow us to read the body more than once in the case that
				// our processed responses doesn't store the cached response correctly against our reference
				const data = (await response.clone().json()) as Data;
				// and remember we've seen this
				processedResponses.set(response, data);
				setState(toData(data));

				fireAnalyticsEvent({
					action: 'resolved',
					actionSubject: 'dataPromise',
					actionSubjectId: camelCase(analyticsSubjectId) + 'DataPromise',
					eventType: 'operational',
					attributes: {
						...analyticsAttributes,
					},
				});
			} catch (err) {
				const errorState = toError(err);

				fireAnalyticsEvent({
					action: 'rejected',
					actionSubject: 'dataPromise',
					actionSubjectId: camelCase(analyticsSubjectId) + 'DataPromise',
					eventType: 'operational',
					attributes: {
						...analyticsAttributes,
						errorName: errorState.error.name ?? 'unknown',
						errorMessage: errorState.error.message ?? 'unknown',
					},
				});

				setState(errorState);
			}
		})();
	}, [promiseToResolve]);

	return state;
};
